import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";

interface PostingPattern {
  byDayOfWeek: Record<string, { count: number; avgEngagement: number }>;
  byHourOfDay: Record<number, { count: number; avgEngagement: number }>;
  optimalPostingTime: string;
}

interface ContentCalendarHeatmapProps {
  postingPatterns: PostingPattern;
}

export const ContentCalendarHeatmap = ({ postingPatterns }: ContentCalendarHeatmapProps) => {
  if (!postingPatterns) {
    return null;
  }

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Calculate max values for color scaling
  const maxEngagement = Math.max(
    ...Object.values(postingPatterns.byDayOfWeek).map(d => d.avgEngagement),
    ...Object.values(postingPatterns.byHourOfDay).map(h => h.avgEngagement)
  );

  const getColorIntensity = (value: number) => {
    if (value === 0 || maxEngagement === 0) return 'bg-muted';
    const intensity = value / maxEngagement;
    if (intensity > 0.8) return 'bg-success/80';
    if (intensity > 0.6) return 'bg-success/60';
    if (intensity > 0.4) return 'bg-warning/60';
    if (intensity > 0.2) return 'bg-warning/40';
    return 'bg-muted';
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <CardTitle>Wzorce Publikacji</CardTitle>
        </div>
        <CardDescription>
          Optymalne dni i godziny publikacji na LinkedIn
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Day of Week Analysis */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Dni tygodnia</h3>
          <div className="grid grid-cols-7 gap-2">
            {days.map((day) => {
              const data = postingPatterns.byDayOfWeek[day] || { count: 0, avgEngagement: 0 };
              const isOptimal = postingPatterns.optimalPostingTime.includes(day);
              
              return (
                <div
                  key={day}
                  className={`p-3 rounded-lg text-center transition-all ${
                    getColorIntensity(data.avgEngagement)
                  } ${isOptimal ? 'ring-2 ring-primary' : ''}`}
                >
                  <p className="text-xs font-medium mb-1">{day.slice(0, 3)}</p>
                  <p className="text-lg font-bold">{data.count}</p>
                  <p className="text-xs text-muted-foreground">
                    {data.avgEngagement > 0 ? `${data.avgEngagement} eng` : '-'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hour of Day Heatmap */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Godziny publikacji (0-23)</h3>
          <div className="grid grid-cols-12 gap-1">
            {hours.map((hour) => {
              const data = postingPatterns.byHourOfDay[hour] || { count: 0, avgEngagement: 0 };
              
              return (
                <div
                  key={hour}
                  className={`aspect-square rounded transition-all ${
                    getColorIntensity(data.avgEngagement)
                  } hover:scale-110 relative group`}
                  title={`${hour}:00 - ${data.count} posts, ${data.avgEngagement} avg engagement`}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-medium opacity-0 group-hover:opacity-100">
                      {hour}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>0:00</span>
            <span>6:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>23:00</span>
          </div>
        </div>

        {/* Optimal Time Highlight */}
        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⭐</span>
            <span className="font-semibold">Optymalny czas publikacji</span>
          </div>
          <p className="text-lg font-bold text-primary">{postingPatterns.optimalPostingTime}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Posty publikowane w tym czasie osiągają najwyższe zaangażowanie
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground">Zaangażowanie:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-muted rounded" />
            <span>Niskie</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-warning/40 rounded" />
            <span>Średnie</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-success/60 rounded" />
            <span>Wysokie</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
